import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { supabase, isSupabaseConfigured } from 'lib/supabase';

const STORAGE_KEY = 'edutracker.behavior_support_plans.v1';
const TABLE = 'behavior_support_plans';

const BehaviorPlansContext = createContext(null);

function readLocal() {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeLocal(plans) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(plans));
  } catch {
    // Ignore storage failures — see IncidentsContext for rationale.
  }
}

function generateLocalId() {
  return `bsp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

// Coerce any incoming value into a real array. Guards against strings or
// other non-array truthies leaking through from localStorage, hand-edited
// migrations, or unexpected Supabase JSON shapes — those would then throw
// on `.map()` / `.filter()` in downstream components.
function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function normalizeIncoming(row) {
  if (!row) return null;
  return {
    id: row.id,
    studentId: row.student_id ?? row.studentId ?? '',
    mtssTier: row.mtss_tier ?? row.mtssTier ?? 2,
    targetBehaviorDefinition:
      row.target_behavior_definition ?? row.targetBehaviorDefinition ?? '',
    fbaSummary: row.fba_summary ?? row.fbaSummary ?? '',
    hypothesizedFunction: row.hypothesized_function ?? row.hypothesizedFunction ?? '',
    hypothesisStatement: row.hypothesis_statement ?? row.hypothesisStatement ?? '',
    replacementBehavior: row.replacement_behavior ?? row.replacementBehavior ?? '',
    antecedentInterventions: asArray(
      row.antecedent_interventions ?? row.antecedentInterventions,
    ),
    consequenceInterventions: asArray(
      row.consequence_interventions ?? row.consequenceInterventions,
    ),
    teachingStrategies: asArray(row.teaching_strategies ?? row.teachingStrategies),
    smartGoal: row.smart_goal ?? row.smartGoal ?? '',
    goalBaseline: row.goal_baseline ?? row.goalBaseline ?? '',
    goalTarget: row.goal_target ?? row.goalTarget ?? '',
    goalMasteryCriteria: row.goal_mastery_criteria ?? row.goalMasteryCriteria ?? '',
    goalTargetDate: row.goal_target_date ?? row.goalTargetDate ?? null,
    progressMonitoringPlan:
      row.progress_monitoring_plan ?? row.progressMonitoringPlan ?? '',
    escalationPlan: row.escalation_plan ?? row.escalationPlan ?? '',
    teamRoles: asArray(row.team_roles ?? row.teamRoles),
    fidelityPlan: row.fidelity_plan ?? row.fidelityPlan ?? '',
    status: row.status ?? 'active',
    effectiveFrom: row.effective_from ?? row.effectiveFrom ?? null,
    nextReviewDate: row.next_review_date ?? row.nextReviewDate ?? null,
    createdAt: row.created_at ?? row.createdAt ?? null,
    lastUpdated: row.last_updated ?? row.lastUpdated ?? null,
  };
}

function toRow(plan) {
  return {
    student_id: plan.studentId,
    mtss_tier: plan.mtssTier ?? 2,
    target_behavior_definition: plan.targetBehaviorDefinition ?? '',
    fba_summary: plan.fbaSummary || null,
    hypothesized_function: plan.hypothesizedFunction || null,
    hypothesis_statement: plan.hypothesisStatement || null,
    replacement_behavior: plan.replacementBehavior || null,
    antecedent_interventions: plan.antecedentInterventions || [],
    consequence_interventions: plan.consequenceInterventions || [],
    teaching_strategies: plan.teachingStrategies || [],
    smart_goal: plan.smartGoal || null,
    goal_baseline: plan.goalBaseline || null,
    goal_target: plan.goalTarget || null,
    goal_mastery_criteria: plan.goalMasteryCriteria || null,
    goal_target_date: plan.goalTargetDate || null,
    progress_monitoring_plan: plan.progressMonitoringPlan || null,
    escalation_plan: plan.escalationPlan || null,
    team_roles: plan.teamRoles || [],
    fidelity_plan: plan.fidelityPlan || null,
    status: plan.status || 'active',
    effective_from: plan.effectiveFrom || null,
    next_review_date: plan.nextReviewDate || null,
  };
}

function normalizeLocal(rows) {
  return rows.map(normalizeIncoming).filter(Boolean);
}

export function BehaviorPlansProvider({ children }) {
  const supabaseLive = isSupabaseConfigured() && supabase !== null;
  const [plans, setPlans] = useState(() =>
    supabaseLive ? [] : normalizeLocal(readLocal()),
  );
  const [loading, setLoading] = useState(supabaseLive);
  const [error, setError] = useState(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    if (!supabaseLive) {
      setPlans(normalizeLocal(readLocal()));
      return;
    }
    setLoading(true);
    setError(null);
    const { data, error: fetchError } = await supabase
      .from(TABLE)
      .select('*')
      .order('last_updated', { ascending: false });
    if (!mounted.current) return;
    if (fetchError) {
      setError(fetchError.message || 'Failed to load behavior support plans');
      setLoading(false);
      return;
    }
    setPlans((data || []).map(normalizeIncoming).filter(Boolean));
    setLoading(false);
  }, [supabaseLive]);

  useEffect(() => {
    if (!supabaseLive) return undefined;
    refresh();
    const channel = supabase
      .channel('public:behavior_support_plans')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: TABLE },
        () => refresh()
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [supabaseLive, refresh]);

  const savePlan = useCallback(
    async (input) => {
      const nowIso = new Date().toISOString();
      const isNew = !input?.id;
      const draft = normalizeIncoming({
        ...input,
        id: input?.id || generateLocalId(),
        created_at: input?.createdAt || nowIso,
        last_updated: nowIso,
      });

      if (!supabaseLive) {
        const current = readLocal();
        const next = isNew
          ? [draft, ...current]
          : current.map((p) => (p.id === draft.id ? draft : p));
        writeLocal(next);
        setPlans(next);
        return draft;
      }

      if (isNew) {
        const { data, error: insertError } = await supabase
          .from(TABLE)
          .insert(toRow(draft))
          .select('*')
          .single();
        if (insertError) {
          setError(insertError.message || 'Failed to save plan');
          throw insertError;
        }
        const saved = normalizeIncoming(data);
        setPlans((prev) => [saved, ...prev.filter((p) => p.id !== saved.id)]);
        return saved;
      }

      const { data, error: updateError } = await supabase
        .from(TABLE)
        .update(toRow(draft))
        .eq('id', draft.id)
        .select('*')
        .single();
      if (updateError) {
        setError(updateError.message || 'Failed to save plan');
        throw updateError;
      }
      const saved = normalizeIncoming(data);
      setPlans((prev) => prev.map((p) => (p.id === saved.id ? saved : p)));
      return saved;
    },
    [supabaseLive]
  );

  const removePlan = useCallback(
    async (id) => {
      if (!supabaseLive) {
        const next = readLocal().filter((p) => p.id !== id);
        writeLocal(next);
        setPlans(next);
        return true;
      }
      const { error: deleteError } = await supabase.from(TABLE).delete().eq('id', id);
      if (deleteError) {
        setError(deleteError.message || 'Failed to delete plan');
        throw deleteError;
      }
      setPlans((prev) => prev.filter((p) => p.id !== id));
      return true;
    },
    [supabaseLive]
  );

  const activePlanForStudent = useCallback(
    (studentId) =>
      plans.find((p) => p.studentId === studentId && p.status !== 'archived') || null,
    [plans]
  );

  const plansForStudent = useCallback(
    (studentId) => plans.filter((p) => p.studentId === studentId),
    [plans]
  );

  const value = useMemo(
    () => ({
      plans,
      loading,
      error,
      isLive: supabaseLive,
      refresh,
      savePlan,
      removePlan,
      activePlanForStudent,
      plansForStudent,
    }),
    [plans, loading, error, supabaseLive, refresh, savePlan, removePlan, activePlanForStudent, plansForStudent]
  );

  return (
    <BehaviorPlansContext.Provider value={value}>{children}</BehaviorPlansContext.Provider>
  );
}

export function useBehaviorPlans() {
  const ctx = useContext(BehaviorPlansContext);
  if (!ctx) {
    throw new Error('useBehaviorPlans must be used within a <BehaviorPlansProvider>');
  }
  return ctx;
}
